import { ScrollText } from '@lucide/vue'

import { AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'
import { TagRef } from '@types'

import {
  amountField,
  assetNameField,
  assetTypeField,
  currencyField,
  dateField,
  descriptionField,
} from '../utils/fields'
import { SUMMARY_STEP, TAGS_STEP, valueStep } from '../utils/steps'
import { INVESTORS_STEP_KEY } from '../utils/const'
import { WizardDefinition } from '../utils/types'

import { privateSecuritySchema } from './schema'
import { create } from './create'
import InvestorsStep from '../components/InvestorsStep.vue'

const DEFAULT_TAGS: TagRef[] = [
  { tag_name: 'Asset Class', tag_value: 'Alternative Investments' },
  { tag_name: 'Investment Strategy', tag_value: 'Private Equity' },
]

/**
 * The catch-all: a private (non-fund) security — private company stock,
 * notes, warrants — or anything else that fits none of the other seven
 * flows. Same split mechanics as PrivateFundSubscribeAndCommit (one cost,
 * divided across investing entities by share/percent, InvestorAllocationBar
 * and all — see InvestorsStep.vue) rather than PrivateFund's own entities
 * step, where each entity independently holds the full amount: a joint stock
 * purchase is usually one purchase split between owners, not several
 * unrelated ones. Unlike that sibling flow, the assistant may suggest a real
 * split here when the sentence states one — "60% through my trust, the rest
 * myself" — but is never required to: a read naming more than one entity
 * with no split stated leaves every row unsized, the same as a manual entry
 * would (see `seedInvestors` and the backend's own `AssetWizardInvestor`).
 * No cash-flows step: unlike the legacy fund wizard, the legacy wizard this
 * replaces never exposed one either (no Contributions/Distributions step in
 * its own STEPS_LIST), so there is no capital-call/distribution history to
 * log here. This one also has no fixed type of its own: the user names it,
 * the one flow that actually renders `assetTypeField` as something to fill
 * in rather than only ever read from a sentence.
 */
export const privateSecurityWizard: WizardDefinition = {
  flow: AssetWizardFlow.PRIVATE_SECURITY,
  label: 'Private security or other',
  icon: ScrollText,
  schema: privateSecuritySchema,
  steps: [
    {
      key: 'security',
      label: 'Asset',
      heading: 'What is this?',
      description: 'Private company stock, notes, warrants — or anything else that fits none of the other wizards.',
      fields: [
        { ...assetTypeField('Type', 'assetTypes'), required: true },
        assetNameField('Name', 'Name this asset'),
        descriptionField(),
      ],
    },
    {
      key: 'investment',
      label: 'Investment',
      heading: 'How much did you invest?',
      description: 'The cost of this investment, and when it was made.',
      fields: [
        amountField('Cost of investment'),
        dateField('Date of investment'),
        currencyField(),
      ],
    },
    {
      key: INVESTORS_STEP_KEY,
      label: 'Investing entities',
      heading: 'Who is investing?',
      description: 'Split the cost above across each entity holding this — one asset and transaction bundle per row.',
      component: InvestorsStep,
    },
    valueStep({
      heading: 'What is it worth?',
      description: 'Record the value and the date it was struck. Each mark dates the value it sets.',
    }),
    TAGS_STEP,
    SUMMARY_STEP,
  ],
  records: [
    {
      key: 'security',
      label: 'Asset',
      icon: ScrollText,
      tone: 'brand',
      transactionType: 'subscribe and fund',
      heading: 'assetName',
      fields: ['amount', 'date', 'currency', 'description'],
    },
  ],
  defaultTags: DEFAULT_TAGS,
  create,
}
